import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Tooltip } from './tooltip'

export const buttonVariants = cva(
  'relative inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-control px-4 text-body font-medium whitespace-nowrap no-underline transition-colors duration-fast disabled:cursor-not-allowed disabled:opacity-60 aria-disabled:cursor-not-allowed aria-disabled:opacity-60 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-action text-on-action hover:bg-action-hover',
        secondary: 'border border-border-control bg-surface text-fg hover:bg-background',
        ghost: 'text-fg hover:bg-border-subtle/50',
        destructive: 'bg-danger text-background hover:opacity-90 dark:text-background',
      },
      size: {
        default: '',
        icon: 'size-11 px-0',
      },
    },
    defaultVariants: { variant: 'primary', size: 'default' },
  },
)

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Keeps width, hides the label visually and blocks repeated submits. */
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading, disabled, children, type = 'button', ...props },
  ref,
) {
  const { t } = useTranslation()
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    >
      <span className={cn('inline-flex items-center justify-center gap-2', loading && 'invisible')}>{children}</span>
      {loading ? (
        <>
          <Loader2 aria-hidden className="absolute size-5 animate-spin motion-reduce:animate-none" />
          <span className="sr-only">{t('common.loading')}</span>
        </>
      ) : null}
    </button>
  )
})

export interface IconButtonProps extends Omit<ButtonProps, 'size' | 'aria-label'> {
  'aria-label': string
}

/** Icon-only button: accessible name is required, tooltip shows on hover-capable devices. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { 'aria-label': label, variant = 'ghost', ...props },
  ref,
) {
  return (
    <Tooltip content={label}>
      <Button ref={ref} size="icon" variant={variant} aria-label={label} {...props} />
    </Tooltip>
  )
})
